import { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { api } from '../api/client';

export function ResetClientPasswordDialog({
  clientId, clientName, open, onClose,
}: { clientId: number | string; clientName?: string; open: boolean; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const reset = useMutation({
    mutationFn: async () => (await api.patch(`/clients/${clientId}/password`, { password })).data.data,
  });

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 8;
  const errMsg =
    (reset.error as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ?? 'Failed to reset password';

  const handleClose = () => { setPassword(''); setConfirm(''); reset.reset(); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>Reset Password</DialogTitle>
      <DialogContent>
        {clientName && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Set a new password for <strong>{clientName}</strong>. Share it securely.
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
          <TextField label="New Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth
            error={tooShort} helperText={tooShort ? 'At least 8 characters.' : ' '} />
          <TextField label="Confirm Password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} fullWidth
            error={mismatch} helperText={mismatch ? 'Passwords do not match.' : ' '} />
          {reset.isError && <Alert severity="error">{errMsg}</Alert>}
          {reset.isSuccess && <Alert severity="success">Password reset successfully.</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">Close</Button>
        <Button variant="contained" onClick={() => reset.mutate()} disabled={reset.isPending || password.length < 8 || mismatch || !confirm}>
          {reset.isPending ? 'Resetting…' : 'Reset Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
